import type { SimulationPlatform } from '@lensora/shared'
import { SIMULATION_PLATFORMS } from '@lensora/shared'
import { cn } from '@lensora/ui'

// Single source of truth for platform identity in the dashboard: label,
// brand colour, icon, and whether the platform is Growth+-gated. Every page
// that renders a platform picker/badge/icon should read from here instead of
// keeping its own list — that's what let 'google-ai' silently go missing
// from pickers and colour maps when it was first added.
//
// Covers SIMULATION_PLATFORMS only — the platforms a tenant can pick to run
// a scan against. 'groq' is excluded on purpose: it's an internal-only
// content-generation fallback (see createContentLLMClient), never shown here.
type PlatformMeta = { label: string; bg: string; svgPath: string; growthGated: boolean }

const PLATFORM_CONFIG: Record<SimulationPlatform, PlatformMeta> = {
  chatgpt: {
    label: 'ChatGPT',
    bg: '#000000',
    svgPath: 'M22.28 9.28a5.998 5.998 0 0 0-.52-4.93 6.07 6.07 0 0 0-6.53-2.91A6.01 6.01 0 0 0 10.71 0a6.07 6.07 0 0 0-5.78 4.2 6.01 6.01 0 0 0-4.01 2.9 6.07 6.07 0 0 0 .75 7.12 5.998 5.998 0 0 0 .52 4.93 6.07 6.07 0 0 0 6.53 2.91A6.01 6.01 0 0 0 13.29 24a6.07 6.07 0 0 0 5.78-4.2 6.01 6.01 0 0 0 4.01-2.9 6.07 6.07 0 0 0-.75-7.12zM13.29 22.4a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.79-2.77a.79.79 0 0 0 .4-.69v-6.77l2.02 1.17a.07.07 0 0 1 .04.06v5.6a4.52 4.52 0 0 1-4.51 4.52zm-9.69-4.14a4.49 4.49 0 0 1-.54-3.02l.14.09 4.79 2.77a.79.79 0 0 0 .79 0l5.85-3.38v2.33a.07.07 0 0 1-.03.06L9.77 20.4a4.52 4.52 0 0 1-6.17-2.14zM2.94 7.9a4.49 4.49 0 0 1 2.35-1.98v5.7a.79.79 0 0 0 .4.68l5.85 3.38-2.02 1.17a.07.07 0 0 1-.07 0L4.72 14A4.52 4.52 0 0 1 2.94 7.9zm16.59 3.87-5.85-3.38 2.02-1.17a.07.07 0 0 1 .07 0l4.73 2.73a4.52 4.52 0 0 1-.7 8.15v-5.7a.79.79 0 0 0-.27-.63zm2.01-3.04-.14-.09-4.79-2.77a.79.79 0 0 0-.79 0L10.97 9.25V6.92a.07.07 0 0 1 .03-.06l4.73-2.73a4.52 4.52 0 0 1 6.17 2.14 4.49 4.49 0 0 1 .54 3.02-.14-.09zM9.73 12.85 7.71 11.68a.07.07 0 0 1-.04-.06V6.02a4.52 4.52 0 0 1 7.41-3.47l-.14.08-4.79 2.77a.79.79 0 0 0-.4.69v6.76zm1.1-2.35 2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5v-3z',
    growthGated: false,
  },
  claude: {
    label: 'Claude',
    bg: '#C96442',
    svgPath: 'M13.827 3.52h-3.645L5.443 20.52h3.644l4.74-17zm4.684 0h-3.645l-4.74 17h3.645l4.74-17z',
    growthGated: false,
  },
  perplexity: {
    label: 'Perplexity',
    bg: '#20B8CD',
    svgPath: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
    growthGated: false,
  },
  gemini: {
    label: 'Gemini',
    bg: '#4285F4',
    svgPath: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z',
    growthGated: false,
  },
  'google-ai': {
    // Google "G" mark — distinct from the Gemini icon since this is a
    // separate, search-grounded platform, not plain Gemini.
    label: 'Google AI Overview',
    bg: '#34A853',
    svgPath: 'M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12s3.36-7.27 7.19-7.27c3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.19 2C6.42 2 1.94 6.8 1.94 12s4.48 10 10.25 10c5.35 0 9.13-3.66 9.13-9.09 0-1.15-.16-1.81-.16-1.81z',
    growthGated: true,
  },
  // Search-grounded variants — same underlying model logomark as the plain
  // platform (still recognizably ChatGPT/Claude), distinguished by a
  // dedicated accent colour + label rather than a different icon, same as
  // OpenAI's/Anthropic's own UI treatment of their search surfaces.
  'chatgpt-search': {
    label: 'ChatGPT Search',
    bg: '#0EA5E9',
    svgPath: 'M22.28 9.28a5.998 5.998 0 0 0-.52-4.93 6.07 6.07 0 0 0-6.53-2.91A6.01 6.01 0 0 0 10.71 0a6.07 6.07 0 0 0-5.78 4.2 6.01 6.01 0 0 0-4.01 2.9 6.07 6.07 0 0 0 .75 7.12 5.998 5.998 0 0 0 .52 4.93 6.07 6.07 0 0 0 6.53 2.91A6.01 6.01 0 0 0 13.29 24a6.07 6.07 0 0 0 5.78-4.2 6.01 6.01 0 0 0 4.01-2.9 6.07 6.07 0 0 0-.75-7.12zM13.29 22.4a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.79-2.77a.79.79 0 0 0 .4-.69v-6.77l2.02 1.17a.07.07 0 0 1 .04.06v5.6a4.52 4.52 0 0 1-4.51 4.52zm-9.69-4.14a4.49 4.49 0 0 1-.54-3.02l.14.09 4.79 2.77a.79.79 0 0 0 .79 0l5.85-3.38v2.33a.07.07 0 0 1-.03.06L9.77 20.4a4.52 4.52 0 0 1-6.17-2.14zM2.94 7.9a4.49 4.49 0 0 1 2.35-1.98v5.7a.79.79 0 0 0 .4.68l5.85 3.38-2.02 1.17a.07.07 0 0 1-.07 0L4.72 14A4.52 4.52 0 0 1 2.94 7.9zm16.59 3.87-5.85-3.38 2.02-1.17a.07.07 0 0 1 .07 0l4.73 2.73a4.52 4.52 0 0 1-.7 8.15v-5.7a.79.79 0 0 0-.27-.63zm2.01-3.04-.14-.09-4.79-2.77a.79.79 0 0 0-.79 0L10.97 9.25V6.92a.07.07 0 0 1 .03-.06l4.73-2.73a4.52 4.52 0 0 1 6.17 2.14 4.49 4.49 0 0 1 .54 3.02-.14-.09zM9.73 12.85 7.71 11.68a.07.07 0 0 1-.04-.06V6.02a4.52 4.52 0 0 1 7.41-3.47l-.14.08-4.79 2.77a.79.79 0 0 0-.4.69v6.76zm1.1-2.35 2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5v-3z',
    growthGated: true,
  },
  'claude-search': {
    label: 'Claude Search',
    bg: '#F59E0B',
    svgPath: 'M13.827 3.52h-3.645L5.443 20.52h3.644l4.74-17zm4.684 0h-3.645l-4.74 17h3.645l4.74-17z',
    growthGated: true,
  },
}

function lookup(platform: string): PlatformMeta | undefined {
  const key = platform.toLowerCase().replace(/\s/g, '') as SimulationPlatform
  return Object.hasOwn(PLATFORM_CONFIG, key) ? PLATFORM_CONFIG[key] : undefined
}

/** Platforms selectable in a dashboard platform picker, in canonical display order. */
export const PICKER_PLATFORMS: readonly SimulationPlatform[] = SIMULATION_PLATFORMS

export function isGrowthGatedPlatform(platform: string): boolean {
  return lookup(platform)?.growthGated ?? false
}

export function platformColor(platform: string): string {
  return lookup(platform)?.bg ?? '#64748b'
}

interface PlatformIconProps {
  platform: string
  size?: number
  dimmed?: boolean
  className?: string
}

export function PlatformIcon({ platform, size = 20, dimmed = false, className }: PlatformIconProps) {
  const config = lookup(platform)
  const initial = (platform[0] ?? '?').toUpperCase()

  if (!config) {
    return (
      <div
        title={platform}
        className={cn('flex shrink-0 items-center justify-center rounded-md bg-surface-overlay font-semibold text-text-muted', className)}
        style={{ width: size, height: size, fontSize: size * 0.45, opacity: dimmed ? 0.3 : 1 }}
      >
        {initial}
      </div>
    )
  }

  return (
    <div
      title={platform}
      className={cn('flex shrink-0 items-center justify-center rounded-md', className)}
      style={{ width: size, height: size, background: config.bg, opacity: dimmed ? 0.25 : 1 }}
    >
      <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="white">
        <path d={config.svgPath} />
      </svg>
    </div>
  )
}

export function formatPlatformName(platform: string): string {
  return lookup(platform)?.label ?? (platform.charAt(0).toUpperCase() + platform.slice(1))
}
